import { range } from 'lodash';
import React from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useSet } from 'react-use';
import { Accordion } from '../../../../../../../CommonComponents/OPL/Accordion/Accordion/Accordion';
import { AccordionButton } from '../../../../../../../CommonComponents/OPL/Accordion/AccordionButton/AccordionButton';
import { Box } from '../../../../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../../../../CommonComponents/OPL/Button/Button';
import { Checkbox } from '../../../../../../../CommonComponents/OPL/Checkbox/Checkbox';
import { IconButton } from '../../../../../../../CommonComponents/OPL/IconButton/IconButton';
import { Select } from '../../../../../../../CommonComponents/OPL/Select/Select/Select';
import { BusinessSaleOptions } from '../../../../api/useWhatIfOptionsQuery';
import { StyledAccordionPanel } from '../../WhatIf.styles';
import { DEFAULT_SALE, FormValues } from './BusinessSale.meta';
import { NetProceedsFromSale } from './components/NetProceedsFromSale/NetProceedsFromSale';

interface BusinessSaleProps {
  options: BusinessSaleOptions;
}

export const BusinessSale = ({ options }: BusinessSaleProps) => {
  const [, { has: isSaleCollapsed, toggle: toggleSale }] = useSet<string | null>();

  const assetOptions = options.Assets.map(opt => ({ label: opt.Label, value: opt.AssetId }));
  const earmarkOptions = options.Earmarks.map(opt => ({ label: opt.Label, value: opt.Value }));

  const {
    register,
    control,
    watch,
    formState: { errors },
  } = useFormContext<FormValues>();

  const { fields, append: appendSale, remove: removeSale } = useFieldArray({
    control,
    name: 'Sales',
  });

  const watchSales = watch('Sales');

  const sales = fields.map((field, index) => ({
    ...field,
    ...watchSales[index],
  }));

  return (
    <>
      <Accordion isActive={false} css="border: none; border-radius: 0; ">
        {sales.map((sale, index) => {
          const saleYears = options.Assets.find(opt => opt.AssetId === sale.AssetId)?.SaleYears;
          const hasCurrentSale =
            options.Assets.find(opt => opt.AssetId === sale.AssetId)?.HasCurrentPlanSale ?? false;

          const yearsOptions = saleYears && [
            { label: 'At retirement', value: null },
            ...range(saleYears.From, saleYears.To).map(year => ({
              label: year.toString(),
              value: year,
            })),
          ];

          return (
            <React.Fragment key={sale.id}>
              <AccordionButton
                id={sale.id}
                panelId={sale.id}
                isActive={!isSaleCollapsed(sale.id)}
                onClick={() => toggleSale(sale.id)}
                extra={
                  sales.length > 1 && (
                    <IconButton
                      variant="subtleInactive"
                      icon="far fa-minus-circle"
                      onClick={() => removeSale(index)}
                    />
                  )
                }
              >
                {options.Assets.find(option => option.AssetId === sale.AssetId)?.Label ??
                  `Business Asset ${index + 1}`}
              </AccordionButton>

              <StyledAccordionPanel
                id={sale.id}
                buttonId={sale.id}
                isActive={!isSaleCollapsed(sale.id)}
              >
                <Box display="flex" flexDirection="column" gridGap="16" px="16" pb="32">
                  <Controller
                    name={`Sales.${index}.AssetId`}
                    control={control}
                    render={({ field: { ref, value, onChange, ...field } }) => (
                      <Select
                        label="Select Business Asset"
                        formVariant={errors.Sales?.[index]?.AssetId && 'error'}
                        helperText={errors.Sales?.[index]?.AssetId?.message}
                        options={assetOptions}
                        value={assetOptions.find(opt => opt.value === value)}
                        onChange={option => {
                          onChange(option?.value);
                        }}
                        innerRef={ref}
                        isPortal
                        disabled={sale.DoNotSell}
                        {...field}
                      />
                    )}
                  />

                  <Controller
                    name={`Sales.${index}.SaleYear`}
                    control={control}
                    render={({ field: { value, onChange, ref, ...field } }) => (
                      <Select
                        label="Sale Year"
                        formVariant={errors.Sales?.[index]?.SaleYear && 'error'}
                        helperText={errors.Sales?.[index]?.SaleYear?.message}
                        options={yearsOptions}
                        value={yearsOptions?.find(option => option.value === value)}
                        onChange={option => {
                          onChange(option?.value);
                        }}
                        innerRef={ref}
                        isPortal
                        disabled={!yearsOptions || sale.DoNotSell}
                        {...field}
                      />
                    )}
                  />

                  <NetProceedsFromSale index={index} disabled={sale.DoNotSell} />

                  <Controller
                    name={`Sales.${index}.Earmarks`}
                    control={control}
                    render={({ field: { ref, onChange, value, ...field } }) => (
                      <Select
                        label="Asset Earmark"
                        options={earmarkOptions}
                        value={earmarkOptions.filter(opt =>
                          value.some(
                            v =>
                              v.EarmarkType === opt.value.EarmarkType &&
                              v.Dependent?.Id === opt.value.Dependent?.Id &&
                              v.Name === opt.value.Name,
                          ),
                        )}
                        onChange={opts => onChange(opts.map(opt => opt.value))}
                        innerRef={ref}
                        isMulti
                        isPortal
                        disabled={sale.DoNotSell}
                        {...field}
                      />
                    )}
                  />

                  {hasCurrentSale && (
                    <Checkbox
                      {...register(`Sales.${index}.DoNotSell`)}
                      disabled={sale.AssetId === null}
                    >
                      What if I choose not to sell this asset?
                    </Checkbox>
                  )}
                </Box>
              </StyledAccordionPanel>
            </React.Fragment>
          );
        })}
      </Accordion>

      {sales.length < assetOptions.length && (
        <Button
          variant="link"
          leftIcon="fas fa-plus"
          onClick={() => appendSale(DEFAULT_SALE)}
          my="24"
          ml="16"
        >
          Add another business asset sale
        </Button>
      )}
    </>
  );
};
